import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { AxiosInstance } from "axios";
// @ts-expect-error pure js
import useAuthenticatedAxios from "./use-authenticated-axios";
// @ts-expect-error pure js
import useDocumentBlob from "./use-document-blob";
// @ts-expect-error pure js
import { useModal } from "./use-modal.jsx";
// @ts-expect-error pure js
import { isImage, isPdf } from "../utils/index.jsx";
// @ts-expect-error pure js
import { DocType } from "../utils/constants/documents";
// @ts-expect-error pure js
import { SignatureContratStatus } from "../models/signatureElectronique";
// @ts-expect-error pure js
import DocumentPreview from "../components/UI/DocumentPreview";
// @ts-expect-error pure js
import Spinner from "../components/UI/Spinner";

export interface DownloadableFile {
  id?: number;
  filename?: string;
  mimetype?: string;
  signature_electronique_status?: string | number | null;
  localFile?: Blob;
}

interface UseDocumentDownloadParams {
  documentId?: number;
  downloadUrl?: string;
  mimetype?: string;
  doctype?: string;
  file?: DownloadableFile;
  isSigned?: boolean;
  save?: boolean;
}

interface UseDocumentDownloadReturn {
  downloadHandler: () => Promise<void>;
  overlay: ReactNode;
  isReady: boolean;
}

export default function useDocumentDownload({
  documentId,
  downloadUrl,
  mimetype,
  doctype = DocType.GENERIC_FILE,
  file,
  isSigned = file?.signature_electronique_status ===
    SignatureContratStatus.Archived.status,
  save,
}: UseDocumentDownloadParams): UseDocumentDownloadReturn {
  const [previewUrl, setPreviewUrl] = useState("");
  const {
    blob,
    isLoading: isBlobDownloading,
    error: errorBlob,
  } = useDocumentBlob({ downloadUrl }) as {
    blob: Blob | undefined;
    isLoading: boolean;
    error: unknown;
  };
  // Preview dans un <dialog> natif (top layer) pour passer au-dessus des
  // modales legacy useModal, elles aussi dans le top layer
  const [PreviewModal, , openPreviewModal] = useModal();
  const axios: AxiosInstance = useAuthenticatedAxios();

  const isImageOrPdf = isImage(mimetype || file) || isPdf(mimetype || file);
  const isNewTab = !save && !isImageOrPdf;
  const isModalPreview = !save && isImageOrPdf;
  const isDownload = save;

  useEffect(() => {
    if (previewUrl && isModalPreview) {
      openPreviewModal();
    }
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isModalPreview, previewUrl, openPreviewModal]);

  function triggerDownload(fileUrl: string) {
    const link = document.createElement("a");
    if (fileUrl.startsWith("blob:")) {
      link.href = fileUrl;
      link.download = "";
    } else {
      link.href = `${fileUrl}&save`;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (fileUrl.startsWith("blob:")) {
      URL.revokeObjectURL(fileUrl);
    }
  }

  async function downloadHandler() {
    let tab: Window | null = null;
    if (isNewTab) {
      tab = window.open("", "_blank");
      if (tab) {
        (tab as unknown as { referer: null }).referer = null;
        tab.opener = null;
      }
    }
    try {
      let fileUrl: string | undefined;
      if (downloadUrl && blob) {
        fileUrl = URL.createObjectURL(blob);
      } else if (file?.id) {
        if (isModalPreview) {
          // Blob same-origin plutôt qu'URL signée api.qalimo.fr : Chrome
          // applique la CSP frame-ancestors de l'API aux <object>/<embed> et
          // bloquerait l'embed cross-origin du PDF
          const response = await axios.get(
            `/documents/${documentId}/download`,
            { params: { doctype, signed: isSigned }, responseType: "blob" },
          );
          fileUrl = URL.createObjectURL(response.data);
        } else {
          const response = await axios.get(
            `/documents/${documentId}/signed-url`,
            { params: { doctype, signed: isSigned } },
          );
          fileUrl = response.data;
        }
      } else if (file?.localFile) {
        fileUrl = URL.createObjectURL(file.localFile);
      }
      if (!fileUrl) return;

      if (isDownload) {
        triggerDownload(fileUrl);
      } else if (isNewTab && tab) {
        tab.location.href = fileUrl;
      } else if (isModalPreview) {
        setPreviewUrl(fileUrl);
      }
    } catch (e) {
      console.error(e);
    }
  }

  const overlay = (
    <>
      {previewUrl && isModalPreview && (
        <PreviewModal noPadding>
          <DocumentPreview
            mimetype={mimetype || file?.mimetype}
            name={file?.filename}
            url={previewUrl}
          />
        </PreviewModal>
      )}
      {(isBlobDownloading || errorBlob) && <Spinner />}
    </>
  );

  return {
    downloadHandler,
    overlay,
    isReady: !isBlobDownloading && !errorBlob,
  };
}
